import React, { useMemo, useState } from "react";
import { QRCodeSVG as QRCode } from "qrcode.react";
import { FaDownload, FaShareAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import "./MovieTicket.css"; // MUST IMPORT THE CSS FILE HERE

const toDisplayDate = (dateValue) => {
  if (!dateValue) return "26th October";
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "26th October";
  return parsedDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
};

const toDisplayTime = (timeValue) => {
  if (!timeValue) return "7:00 PM - 10:00 PM";
  if (
    String(timeValue).toLowerCase().includes("am") ||
    String(timeValue).toLowerCase().includes("pm")
  ) {
    return String(timeValue).toUpperCase();
  }
  const [hoursPart, minutesPart = "00"] = String(timeValue).split(":");
  const hours = Number.parseInt(hoursPart, 10);
  const minutes = Number.parseInt(minutesPart, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return String(timeValue);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const getTicketId = (booking) => {
  if (booking?.ticketId) return booking.ticketId;
  if (booking?.bookingId)
    return `TKT-${String(booking.bookingId).slice(-8).toUpperCase()}`;
  if (booking?._id) return `TKT-${String(booking._id).slice(-8).toUpperCase()}`;
  return `* * * * *`;
};

const MovieTicket = ({ booking, onDownload }) => {
  const [shareStatus, setShareStatus] = useState("");

  const ticketId = useMemo(() => getTicketId(booking), [booking]);

  // Content mapped exactly to your image design
  const eventTitle =
    booking?.show?.movie?.title ||
    booking?.movieTitle ||
    "Dil-Luminati Tour\nINDIA 2024";
  const eventBanner =
    booking?.show?.movie?.poster ||
    booking?.moviePoster ||
    "https://images.unsplash.com/photo-1540039155732-d68a2ee36c2e?auto=format&fit=crop&w=800&q=80";
  const viewerName =
    booking?.userName ||
    booking?.user?.name ||
    booking?.customerName ||
    "Dinesh Awtani";
  const theaterName =
    booking?.show?.theater?.name ||
    booking?.theaterName ||
    "JN Stadium, Pragati Vihar";
  const cityName =
    booking?.show?.theater?.address?.city ||
    booking?.show?.theater?.city ||
    booking?.city ||
    "New Delhi";
  const showDate = toDisplayDate(booking?.showDate || booking?.show?.showDate);
  const showTime = toDisplayTime(booking?.showTime || booking?.show?.showTime);
  const screenNumber = booking?.show?.screenNumber || booking?.screenNumber;
  const gateLabel = screenNumber ? `Gate ${screenNumber}` : "C End";

  const qrData = booking?.tickets?.[0]?.qrToken || `TICKET:${ticketId}|BOOKING:${booking?.bookingId || "N/A"}|EVENT:${eventTitle.replace("\n", " ")}|VENUE:${theaterName}`;

  const shareTicket = async () => {
    const sharePayload = {
      title: `${eventTitle.replace("\n", " ")} Ticket`,
      text: `Ticket ${ticketId} for ${eventTitle.replace("\n", " ")} at ${theaterName} on ${showDate}, ${showTime}.`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        setShareStatus("Ticket shared successfully.");
      } else {
        await navigator.clipboard.writeText(
          `${sharePayload.text}\n${sharePayload.url}`,
        );
        setShareStatus("Ticket details copied.");
      }
    } catch (error) {
      setShareStatus("Unable to share ticket.");
    }
    window.setTimeout(() => setShareStatus(""), 2200);
  };

  const downloadTicket = () => {
    if (typeof onDownload === "function") {
      onDownload();
      return;
    }
    window.print();
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="ticket-page-wrapper"
    >
      <div className="luminati-ticket">
        {/* Top Half of Ticket */}
        <div className="ticket-top-section">
          <motion.img
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            src={eventBanner}
            alt="Event Cover"
            className="ticket-cover-img"
          />

          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="ticket-heading"
          >
            {eventTitle.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index === 0 && <br />}
              </React.Fragment>
            ))}
          </motion.h1>

          {(() => {
            const selectedSeats = Array.isArray(booking?.seats) 
              ? booking.seats.join(", ") 
              : (booking?.seats || "N/A");

            return (
              <div className="ticket-info-grid">
                {[
                  { label: "NAME", value: viewerName },
                  { label: "GATE", value: gateLabel, align: "right" },
                  { label: "SCREEN", value: screenNumber || "1" },
                  { label: "SEATS", value: selectedSeats, align: "right" },
                  { label: "TIME", value: showTime },
                  { label: "DATE", value: showDate, align: "right" }
                ].map((item, i) => (
                  <motion.div 
                    key={item.label}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -10 : 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + (i * 0.05) }}
                    className={`info-block ${item.align === "right" ? "align-right" : ""}`}
                  >
                    <span className="info-label">{item.label}</span>
                    <span className="info-value">{item.value}</span>
                  </motion.div>
                ))}

                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="info-block full-width"
                >
                  <span className="info-label">PLACE</span>
                  <span className="info-value">{`${theaterName}, ${cityName}`}</span>
                </motion.div>
              </div>
            );
          })()}
        </div>

        {/* The Punched Hole / Dashed Divider Line */}
        <div className="ticket-punch-line">
          <div className="punch-hole hole-left"></div>
          <div className="dashed-separator"></div>
          <div className="punch-hole hole-right"></div>
        </div>

        {/* Bottom Half of Ticket */}
        <div className="ticket-bottom-section">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="qr-container-card"
          >
            <div className="qr-text-side">
              <p className="qr-instruction">
                Scan this QR code
                <br />
                or show this ticket
                <br />
                at entry
              </p>
              <p className="ticket-id-display">
                Ticket ID: <span className="id-dots">{ticketId}</span>
              </p>
            </div>
            <div className="qr-code-side">
              <QRCode
                value={qrData}
                size={90}
                level="H"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
          </motion.div>

          <div className="ticket-buttons-row">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="action-btn share-btn"
              onClick={shareTicket}
              title="Share"
            >
              <FaShareAlt size={18} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="action-btn download-btn"
              onClick={downloadTicket}
            >
              Download Ticket
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {shareStatus && (
          <motion.div 
            key="toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="toast-message"
          >
            {shareStatus}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MovieTicket;
